/**
 * Cell-coercion helpers used by every format parser.
 *
 * Why these live in their own module:
 *   The same "is this a dollar amount?", "is this a date?" routines
 *   apply to NAPA TRACS / VAST / Tekmetric / Shop-Ware just as much
 *   as to Generic CSV. Keeping them adapter-agnostic prevents each
 *   future adapter from reinventing its own quirky parser.
 *
 * All helpers return `null` on parse failure rather than throwing —
 * the caller decides whether a null is a warning, an error, or
 * silently acceptable for that field.
 */

const CURRENCY_STRIP_RE = /[^0-9.\-(),]/g;

/** Trim and collapse whitespace; map empty string to null. */
export function coerceString(raw: string | undefined | null): string | null {
  if (raw === undefined || raw === null) return null;
  const trimmed = raw.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/**
 * Parse a currency value into integer cents. Accepts:
 *   - "$1,234.56" → 123456
 *   - "1234.56"   → 123456
 *   - "(45.00)"   → -4500  (accounting parens = negative)
 *   - "1,234,56"  → null   (European decimal comma is rejected on
 *                            purpose: prompts the UI to surface a
 *                            "decimal separator" hint rather than
 *                            silently mis-parsing as $123,456.)
 */
export function coerceCents(raw: string | undefined | null): number | null {
  const s = coerceString(raw);
  if (s === null) return null;
  const negative = s.startsWith('(') && s.endsWith(')');
  const inner = negative ? s.slice(1, -1) : s;
  const stripped = inner.replace(CURRENCY_STRIP_RE, '');
  if (stripped.length === 0) return null;
  // Reject ambiguous decimal-comma (e.g. "1.234,56")
  if (/^\d{1,3}(\.\d{3})+,\d{2}$/.test(stripped)) return null;
  const normalized = stripped.replace(/,/g, '');
  const n = Number(normalized);
  if (!Number.isFinite(n)) return null;
  const cents = Math.round(n * 100);
  return negative ? -cents : cents;
}

/** Parse a plain integer (no commas, no decimals). */
export function coerceInteger(raw: string | undefined | null): number | null {
  const s = coerceString(raw);
  if (s === null) return null;
  const cleaned = s.replace(/,/g, '');
  if (!/^-?\d+$/.test(cleaned)) {
    // Try float that rounds to integer (e.g. "12345.0" or "12,345")
    const n = Number(cleaned);
    if (!Number.isFinite(n)) return null;
    return Math.trunc(n);
  }
  return Number(cleaned);
}

/** Parse a decimal hours value ("1.25", "1:15" → 1.25). */
export function coerceHours(raw: string | undefined | null): number | null {
  const s = coerceString(raw);
  if (s === null) return null;
  const colon = s.match(/^(\d+):(\d{1,2})$/);
  if (colon) {
    const h = Number(colon[1]);
    const m = Number(colon[2]);
    if (!Number.isFinite(h) || !Number.isFinite(m) || m >= 60) return null;
    return h + m / 60;
  }
  const n = Number(s.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * Parse a wide range of date/time formats into ISO-8601. We try
 * native Date parsing first; if that fails we try a handful of
 * common DMS-export formats. We deliberately do NOT do
 * locale-sensitive parsing — ambiguous "01/02/2026" inputs are
 * assumed US-style (Jan 2) because every Slice 2 target DMS is
 * North American. The remap dialog (Slice 2.4) will let users
 * override.
 */
export function coerceTimestamp(raw: string | undefined | null): string | null {
  const s = coerceString(raw);
  if (s === null) return null;

  // ISO-ish — let Date handle it.
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }

  // M/D/YYYY [HH:MM[:SS] [AM|PM]]
  const usMatch = s.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM|am|pm)?)?$/,
  );
  if (usMatch) {
    const [, mo, da, yr, hh, mm, ss, ap] = usMatch;
    let year = Number(yr);
    if (year < 100) year += year < 70 ? 2000 : 1900;
    let hours = Number(hh ?? '0');
    if (ap) {
      const upper = ap.toUpperCase();
      if (upper === 'PM' && hours < 12) hours += 12;
      if (upper === 'AM' && hours === 12) hours = 0;
    }
    const d = new Date(Date.UTC(year, Number(mo) - 1, Number(da), hours, Number(mm ?? '0'), Number(ss ?? '0')));
    if (!Number.isNaN(d.getTime())) return d.toISOString();
  }

  // Fallback: native parser.
  const fallback = new Date(s);
  if (!Number.isNaN(fallback.getTime())) return fallback.toISOString();
  return null;
}

/**
 * Parse a yes/no / true/false / 1/0 / Y/N value. Returns false for
 * empty cells (a missing "warranty" flag is not "this might be a
 * warranty job").
 */
export function coerceBoolean(raw: string | undefined | null): boolean {
  const s = coerceString(raw);
  if (s === null) return false;
  const lower = s.toLowerCase();
  return ['y', 'yes', 'true', '1', 't'].includes(lower);
}

/** Normalize a VIN: uppercase, strip spaces. Does NOT validate length. */
export function coerceVin(raw: string | undefined | null): string | null {
  const s = coerceString(raw);
  if (s === null) return null;
  return s.replace(/\s+/g, '').toUpperCase();
}
